import React, { useEffect, useRef, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import {
  EMAIL_ERROR,
  MOBILE_ERROR,
  formatWalkInMobileForInput,
  isValidWalkInEmail,
  parseWalkInMobile,
} from "../../../helpers/walkInValidation";

export const UPDATED_MESSAGE = "Your details have been updated.";
export const GONE_MESSAGE = "Your queue entry is no longer active.";
export const PARTY_SIZE_NOTE =
  "Need to change your party size? Please let the host know.";

const MAX_NAME_LENGTH = 50;
const REQUIRED_ERROR = "This field is required";
const EMPTY_VALUES = {
  firstName: "",
  lastName: "",
  email: "",
  mobileNumber: "",
};

const errorStatus = (error) => error?.response?.status;
const errorMessage = (error) =>
  error?.response?.data?.error_message ||
  "Something went wrong. Please try again.";

/**
 * Lets a queued guest fix a typo in their name, email or mobile number
 * without leaving the queue. Authorised by the token they got when joining.
 * Party size is read-only: changing it moves them between queue groups, so
 * the host does that.
 */
export default function EditWalkInDetailsModal({
  show,
  onHide,
  api,
  storeId,
  queueData,
  onUpdated,
  onGone,
}) {
  const { redisKey, cancelToken } = queueData || {};
  const [status, setStatus] = useState("loading"); // loading | ready | saving
  const [values, setValues] = useState(EMPTY_VALUES);
  const [initial, setInitial] = useState(null);
  const [partySize, setPartySize] = useState("");
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");

  // Callbacks change identity every parent render; keep them out of effect deps.
  const callbacks = useRef({ onHide, onGone });
  callbacks.current = { onHide, onGone };

  useEffect(() => {
    if (!show) return undefined;

    let cancelled = false;
    setStatus("loading");
    setErrors({});
    setServerError("");

    (async () => {
      try {
        const { data } = await api.post({
          url: `/branded-website/reservation/${storeId}/walk-in/details`,
          data: { redisKey, cancelToken },
        });
        if (cancelled) return;

        const { details } = data;
        setInitial({
          firstName: details.firstName,
          lastName: details.lastName,
          email: details.email,
          e164: details.contact,
        });
        setValues({
          firstName: details.firstName,
          lastName: details.lastName,
          email: details.email,
          mobileNumber: formatWalkInMobileForInput(details.contact),
        });
        setPartySize(String(details.partySize));
        setStatus("ready");
      } catch (error) {
        if (cancelled) return;
        if (errorStatus(error) === 404) {
          callbacks.current.onGone();
          callbacks.current.onHide();
          return;
        }
        setServerError(errorMessage(error));
        setStatus("ready");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [show, api, storeId, redisKey, cancelToken]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setValues((current) => ({ ...current, [name]: value }));
    setErrors((current) => {
      if (!current[name]) return current;
      const { [name]: _cleared, ...rest } = current;
      return rest;
    });
    setServerError("");
  };

  const validate = (trimmed, mobile) => {
    const found = {};

    if (!trimmed.firstName && !trimmed.lastName) {
      found.firstName = "Either last name or first name should be provided.";
    }
    ["firstName", "lastName"].forEach((field) => {
      if (trimmed[field].length > MAX_NAME_LENGTH) {
        found[field] = `Must be ${MAX_NAME_LENGTH} characters or fewer.`;
      }
    });
    if (trimmed.email || initial.email) {
      if (!trimmed.email) found.email = REQUIRED_ERROR;
      else if (!isValidWalkInEmail(trimmed.email)) found.email = EMAIL_ERROR;
    }
    if (!trimmed.mobileNumber) found.mobileNumber = REQUIRED_ERROR;
    else if (!mobile.valid) found.mobileNumber = MOBILE_ERROR;

    return found;
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (status !== "ready" || !initial) return;

    const trimmed = {
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      email: values.email.trim(),
      mobileNumber: values.mobileNumber.trim(),
    };
    const mobile = parseWalkInMobile(trimmed.mobileNumber);

    const found = validate(trimmed, mobile);
    if (Object.keys(found).length) {
      setErrors(found);
      return;
    }

    // Send only what changed. The number is compared by its normalised form,
    // so retyping the same number in another format isn't an edit.
    const changes = {};
    if (trimmed.firstName !== initial.firstName) {
      changes.firstName = trimmed.firstName;
    }
    if (trimmed.lastName !== initial.lastName) {
      changes.lastName = trimmed.lastName;
    }
    if (trimmed.email.toLowerCase() !== initial.email.toLowerCase()) {
      changes.email = trimmed.email;
    }
    if (mobile.e164 !== initial.e164) {
      changes.mobileNumber = trimmed.mobileNumber;
    }

    if (!Object.keys(changes).length) {
      onHide();
      return;
    }

    setStatus("saving");
    try {
      const { data } = await api.put({
        url: `/branded-website/reservation/${storeId}/walk-in`,
        data: { redisKey, cancelToken, ...changes },
      });
      onUpdated(data.details);
      onHide();
    } catch (error) {
      if (errorStatus(error) === 404) {
        onGone();
        onHide();
        return;
      }
      setServerError(errorMessage(error));
      setStatus("ready");
    }
  };

  const busy = status !== "ready";

  const field = (name, label, extra = {}) => (
    <Form.Group controlId={`walkin-edit-${name}`}>
      <Form.Label>{label}</Form.Label>
      <Form.Control
        name={name}
        value={values[name]}
        onChange={handleChange}
        disabled={busy}
        isInvalid={Boolean(errors[name])}
        {...extra}
      />
      <Form.Control.Feedback type="invalid">
        {errors[name]}
      </Form.Control.Feedback>
    </Form.Group>
  );

  return (
    <Modal show={show} onHide={onHide} centered animation={false}>
      <Form onSubmit={handleSave} noValidate>
        <Modal.Header closeButton>
          <Modal.Title>Edit my details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {status === "loading" ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <>
              {field("firstName", "First name")}
              {field("lastName", "Last name")}
              {field("email", "Email", { type: "email" })}
              {field("mobileNumber", "Mobile number", { type: "tel" })}
              <Form.Group controlId="walkin-edit-partySize">
                <Form.Label>Party size</Form.Label>
                <Form.Control value={partySize} disabled readOnly />
                <Form.Text className="text-muted">{PARTY_SIZE_NOTE}</Form.Text>
              </Form.Group>
              {serverError && (
                <div className="alert alert-danger mb-0" role="alert">
                  {serverError}
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="light"
            onClick={onHide}
            disabled={status === "saving"}
          >
            Close
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={busy}
            style={{ backgroundColor: "#8950fc", borderColor: "#8950fc" }}
          >
            {status === "saving" ? "Saving…" : "Save changes"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
